import React, { Component, ErrorInfo, ReactNode } from 'react';
import HyperDX from '@hyperdx/browser';
import { isHyperDXInitialized } from '@utils/hyperdx';

interface Props {
	children: ReactNode;
	fallback?: ReactNode;
}

interface State {
	hasError: boolean;
	error?: Error;
}

export class HyperDXErrorBoundary extends Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError(error: Error): State {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		// Always log errors to console for debugging (even in development)
		console.error('Error caught by boundary:', error, errorInfo);
		
		// Only send to HyperDX if it's initialized (production/staging)
		if (isHyperDXInitialized()) {
			// HyperDX automatically captures console errors and unhandled exceptions
			// Add additional context for debugging
			console.error('Component Stack:', errorInfo.componentStack);
			console.error('Error Boundary Source:', 'react-error-boundary');
			
			// You can also manually trigger an error event if needed
			window.dispatchEvent(new ErrorEvent('error', {
				error: error,
				message: error.message,
				filename: 'ErrorBoundary',
			}));
		}
	}

	render() {
		if (this.state.hasError) {
			return (
				this.props.fallback || (
					<div style={{
						padding: '20px',
						textAlign: 'center',
						backgroundColor: '#f8f9fa',
						border: '1px solid #dee2e6',
						borderRadius: '8px',
						margin: '20px',
					}}>
						<h2 style={{ color: '#dc3545', marginBottom: '10px' }}>Something went wrong.</h2>
						<p style={{ color: '#6c757d', marginBottom: '20px' }}>
							We've been notified about this error and are working to fix it.
						</p>
						<button 
							onClick={() => window.location.reload()}
							style={{
								backgroundColor: '#007bff',
								color: 'white',
								border: 'none',
								padding: '10px 20px',
								borderRadius: '4px',
								cursor: 'pointer',
							}}
						>
							Reload Page
						</button>
					</div>
				)
			);
		}

		return this.props.children;
	}
} 